import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "reactstrap";
import { ArrowLeft, Clock, Link as LinkIcon, Phone } from "react-feather";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { MixpanelEvents, trackEvent } from "../../../utils/mixpanelTracking";
import type { DetailViewOpsLocationState, DemoLead } from "./demoOps.lib";
import {
  DEMO_STATUS_OPTIONS,
  callStatusSelectOptions,
  getDemoOpsSession,
  loadDemoOpsLeadsPage,
  notConductedReasonSelectOptions,
  persistLeadWithAudit,
  resolveUpdateAgentId,
} from "./demoOps.lib";
import {
  fetchDemoOpsLeadHistory,
  getDemoOpsDemoClassLink,
  postDemoOpsClickToCall,
} from "../../../Api/apiUtils";
import "./demo-ops.scss";
import {
  Badge,
  Button,
  Card,
  DescriptionList,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  Select,
  Table,
} from "../../../ui";
import { TextArea } from "./ui-pending/TextArea";

function isPaidTrial(lead?: DemoLead): boolean {
  return `${lead?.trialType ?? ""}`.trim().toLowerCase() === "paid";
}

function readLeadString(lead: DemoLead | undefined, key: string): string {
  if (!lead) return "";
  const v = (lead as Record<string, unknown>)[key];
  return v != null && String(v) !== "" ? String(v) : "";
}

function classifyTime(lead?: DemoLead) {
  if (!lead?.classDateInTimeZone) {
    const d = `${lead?.slotDate ?? ""}`.trim();
    const t = `${lead?.startTime ?? ""}`.trim();
    if (!d && !t) return "—";
    const m = moment(`${d} ${t}`.trim());
    return m.isValid()
      ? m.format("DD-MMM-YYYY hh:mm:ss A")
      : `${d} ${t}`.trim();
  }
  return moment(lead.classDateInTimeZone).isValid()
    ? moment(lead.classDateInTimeZone).format("DD-MMM-YYYY hh:mm:ss A")
    : String(lead.classDateInTimeZone);
}

export const DetailViewOps: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const state = (location.state || {}) as DetailViewOpsLocationState;

  const session = useMemo(() => getDemoOpsSession(), []);
  const [lead, setLead] = useState<DemoLead | undefined>(state.lead);

  const [draft, setDraft] = useState<DemoLead | undefined>(state.lead);
  const [loading, setLoading] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState<
    { field: string; old_value: string; new_value: string; createdOn?: string }[]
  >([]);
  const [callOpen, setCallOpen] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);

  const viewName = state.viewName ?? "myleads";
  /** Allow update whenever we have a persisted lead row (My Leads, All Leads, or Live Demo detail). */
  const canEditStatuses = !!draft?.recordId;

  const refresh = useCallback(async () => {
    const rid = state.recordId;
    if (!rid) return;
    setLoading(true);
    try {
      let raw: Record<string, unknown>;
      if (viewName === "allleads" && state.fromDate && state.toDate) {
        raw = {
          fromDate: state.fromDate,
          toDate: state.toDate,
          recordId: rid,
          filter: "",
          filterOn: "",
          page: 0,
          size: 10,
        };
      } else {
        raw = {
          agentId: state.agentId || session.agentId,
          recordId: rid,
          filter: "",
          filterOn: "",
          page: 0,
          size: 10,
        };
      }
      const data = await loadDemoOpsLeadsPage(raw);
      const row = (data.content ?? [])[0];
      if (row) {
        setLead(row);
        setDraft(row);
      }
    } catch {
      toast.error("Could not refresh lead.");
    } finally {
      setLoading(false);
    }
  }, [
    session.agentId,
    state.agentId,
    state.fromDate,
    state.recordId,
    state.toDate,
    viewName,
  ]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const sid = String(draft?.studentId ?? "");
    if (!historyOpen || !sid) return;
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      try {
        const data = await fetchDemoOpsLeadHistory(sid);
        if (!cancelled) setHistoryRows(data as { field: string; old_value: string; new_value: string; createdOn?: string }[]);
      } catch {
        if (!cancelled) setHistoryRows([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [historyOpen, draft?.studentId]);

  const goBack = () => {
    navigate(`${process.env.PUBLIC_URL}/DemoOps`, {
      state: {
        activeTab: state.activeTab ?? "allleads",
      },
    });
  };

  const displayNameCell = `${draft?.firstName ?? ""} ${draft?.lastName ?? ""}`.trim();

  const copyLink = async () => {
    if (!draft?.classScheduleId || !draft.studentId) {
      toast.error("Missing identifiers for demo link.");
      return;
    }
    try {
      const url = await getDemoOpsDemoClassLink(
        draft.classScheduleId,
        draft.studentId
      );
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy link failed.");
    }
  };

  const bridgeCall = async () => {
    if (!draft) return;
    const agentNum = `${draft.agentContactNumber ?? session.agentPhone ?? ""}`.replace(
      /\s/g,
      ""
    );
    const cust = `${draft.countryCode ?? ""}${draft.mobileNumber ?? ""}`.trim();
    if (!agentNum || !cust) {
      toast.error("Missing phone.");
      setCallOpen(false);
      return;
    }
    try {
      await postDemoOpsClickToCall({
        agent: agentNum,
        customer: cust.replace(/\s/g, ""),
        userName: session.displayName || "Agent",
      });
      toast.success("Call bridged.");
    } catch {
      toast.error("Call failed.");
    } finally {
      setCallOpen(false);
    }
  };

  const runSave = async () => {
    if (!lead || !draft) return;
    setSaveBusy(true);
    try {
      const agentIdSave = resolveUpdateAgentId(
        draft,
        viewName,
        session.agentId
      );
      const touched =
        lead.demoStatus !== draft.demoStatus
          ? ("demo" as const)
          : lead.demoCallStatus1 !== draft.demoCallStatus1
          ? ("call1" as const)
          : lead.demoCallStatus2 !== draft.demoCallStatus2
          ? ("call2" as const)
          : ("misc" as const);
      await persistLeadWithAudit(lead, draft, {
        agentIdSave,
        displayName: session.displayName || "Agent",
        sessionAgentId: session.agentId,
        touched,
      });
      toast.success("Saved.");
      trackEvent(MixpanelEvents.DEMOOPS_LEAD_SAVE_SUCCESS, {
        demo_ops_source: "lead_detail",
        demo_ops_view: viewName,
        demo_ops_touched: touched,
        record_id: lead.recordId != null ? String(lead.recordId) : "",
        student_id: lead.studentId != null ? String(lead.studentId) : "",
      });
      await refresh();
    } catch {
      toast.error("Save failed.");
      trackEvent(MixpanelEvents.DEMOOPS_LEAD_SAVE_FAILED, {
        demo_ops_source: "lead_detail",
        demo_ops_view: viewName,
        record_id: lead.recordId != null ? String(lead.recordId) : "",
        student_id: lead.studentId != null ? String(lead.studentId) : "",
      });
    } finally {
      setSaveBusy(false);
      setConfirmSave(false);
    }
  };

  const dirty =
    draft &&
    lead &&
    `${draft.demoStatus ?? ""}${draft.demoCallStatus1 ?? ""}${draft.demoCallStatus2 ?? ""}${draft.agentComments ?? ""}${draft.notConductedReason ?? ""}` !==
      `${lead.demoStatus ?? ""}${lead.demoCallStatus1 ?? ""}${lead.demoCallStatus2 ?? ""}${lead.agentComments ?? ""}${lead.notConductedReason ?? ""}`;

  const teacherLabel =
    `${draft?.classStatus ?? ""}`.toLowerCase() === "found" ? (
      <Badge tone="warning">AE not found</Badge>
    ) : (
      `${draft?.teacherFirstName ?? ""} ${draft?.teacherLastName ?? ""}`.trim() || "—"
    );

  const backButton = (
    <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden />} onClick={goBack}>
      Back to Demo Ops
    </Button>
  );

  if (!lead && loading) {
    return (
      <Page className="demo-ops">
        <Loader label="Loading lead…" />
      </Page>
    );
  }

  if (!draft && !lead) {
    return (
      <Page className="demo-ops">
        <EmptyState
          title="Lead not provided."
          description="Open a lead from the My Leads, All Leads or Live Demo list."
          action={
            <Button variant="secondary" onClick={goBack}>
              Back
            </Button>
          }
        />
      </Page>
    );
  }

  const selectOptions = (values: string[]) =>
    values.map((o) => ({ value: o, label: o || "—" }));

  return (
    <Page className="demo-ops">
      <PageHeader
        eyebrow="Demo Ops"
        title={displayNameCell || "Lead detail"}
        subtitle={
          <span className="demo-ops-detail-subtitle">
            {isPaidTrial(draft) ? (
              <Badge tone="accent" solid>
                Paid
              </Badge>
            ) : null}
            <span>
              Record {!draft?.recordId ? "—" : String(draft.recordId)}{" "}
              · Student {!draft?.studentId ? "—" : String(draft.studentId)}
            </span>
          </span>
        }
        actions={
          <>
            {backButton}
            <Button
              size="sm"
              variant="secondary"
              icon={<LinkIcon size={14} aria-hidden />}
              onClick={() => void copyLink()}
            >
              Copy link
            </Button>
            <Button
              size="sm"
              variant="secondary"
              icon={<Phone size={14} aria-hidden />}
              onClick={() => setCallOpen(true)}
            >
              Call
            </Button>
            <Button
              size="sm"
              variant="secondary"
              icon={<Clock size={14} aria-hidden />}
              disabled={!draft?.studentId}
              onClick={() => setHistoryOpen(true)}
            >
              Lead history
            </Button>
          </>
        }
      />

      <Card title="Lead details">
        {loading ? <Loader label="Refreshing lead…" /> : null}
        <DescriptionList
          items={[
            { label: "Agent", value: draft?.agentName ?? "—" },
            { label: "Timezone", value: draft?.studentTimezone ?? "—" },
            {
              label: "Mobile",
              value: `${draft?.countryCode ?? ""}${draft?.mobileNumber ?? ""}` || "—",
            },
            {
              label: "Student ID",
              value: draft?.studentId != null ? String(draft.studentId) : "—",
            },
            { label: "Age group", value: draft?.ageGroup ?? "—" },
            { label: "Country", value: draft?.country ?? "—" },
            { label: "Email", value: readLeadString(draft, "email") || "—" },
            { label: "State", value: readLeadString(draft, "state") || "—" },
            {
              label: "Trial type",
              value: draft?.trialType ? String(draft.trialType) : "—",
            },
            { label: "Course", value: draft?.courseName ?? "—" },
            { label: "Teacher", value: teacherLabel },
            { label: "Class time", value: classifyTime(draft) },
          ]}
        />
      </Card>

      <Card title="Update demo">
        {canEditStatuses ? (
          <div className="ds-stack demo-ops-update">
            <div className="demo-ops-form-grid">
              <Field label="Demo status">
                {(ctl) => (
                  <Select
                    {...ctl}
                    size="sm"
                    value={String(draft?.demoStatus ?? "")}
                    onChange={(e) =>
                      setDraft((d) =>
                        d ? { ...d, demoStatus: e.target.value } : d
                      )
                    }
                    options={[
                      { value: "", label: "—" },
                      ...DEMO_STATUS_OPTIONS.map((o) => ({ value: o, label: o })),
                    ]}
                  />
                )}
              </Field>
              <Field label="Demo call status 1">
                {(ctl) => (
                  <Select
                    {...ctl}
                    size="sm"
                    value={String(draft?.demoCallStatus1 ?? "")}
                    onChange={(e) =>
                      setDraft((d) =>
                        d ? { ...d, demoCallStatus1: e.target.value } : d
                      )
                    }
                    options={selectOptions(callStatusSelectOptions(draft?.demoCallStatus1))}
                  />
                )}
              </Field>
              <Field label="Demo call status 2">
                {(ctl) => (
                  <Select
                    {...ctl}
                    size="sm"
                    value={String(draft?.demoCallStatus2 ?? "")}
                    onChange={(e) =>
                      setDraft((d) =>
                        d ? { ...d, demoCallStatus2: e.target.value } : d
                      )
                    }
                    options={selectOptions(callStatusSelectOptions(draft?.demoCallStatus2))}
                  />
                )}
              </Field>
              <Field label="Not conduct reason">
                {(ctl) => (
                  <Select
                    {...ctl}
                    size="sm"
                    value={String(draft?.notConductedReason ?? "")}
                    onChange={(e) =>
                      setDraft((d) =>
                        d
                          ? { ...d, notConductedReason: e.target.value }
                          : d
                      )
                    }
                    options={selectOptions(
                      notConductedReasonSelectOptions(draft?.notConductedReason)
                    )}
                  />
                )}
              </Field>
            </div>

            <Field label="Agent comments">
              {(ctl) => (
                <TextArea
                  {...ctl}
                  rows={3}
                  value={String(draft?.agentComments ?? "")}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, agentComments: e.target.value } : d
                    )
                  }
                />
              )}
            </Field>

            <div className="demo-ops-form-footer">
              {!dirty ? (
                <span className="ds-text-subtle">
                  Change a field above to enable save.
                </span>
              ) : null}
              <Button
                variant="primary"
                disabled={!dirty || saveBusy || !lead}
                onClick={() => setConfirmSave(true)}
              >
                Save changes
              </Button>
            </div>
          </div>
        ) : (
          <div className="ds-stack demo-ops-update">
            <DescriptionList
              items={[
                { label: "Demo status", value: draft?.demoStatus ?? "—" },
                { label: "Demo call status 1", value: draft?.demoCallStatus1 ?? "—" },
                { label: "Demo call status 2", value: draft?.demoCallStatus2 ?? "—" },
                { label: "Not conduct reason", value: draft?.notConductedReason ?? "—" },
                { label: "Agent comments", value: draft?.agentComments ?? "—" },
              ]}
            />
            <p className="ds-text-subtle demo-ops-note">
              This lead has no record id — open from the leads list to update.
            </p>
          </div>
        )}
      </Card>

      <Modal isOpen={callOpen} toggle={() => setCallOpen(false)} centered className="ds-dialog">
        <h2 className="ds-dialog__title">Bridge call</h2>
        <div className="ds-dialog__body">
          Dial{" "}
          <strong>{`${draft?.countryCode ?? ""}${draft?.mobileNumber ?? ""}`}</strong>
          ?
        </div>
        <div className="ds-dialog__actions">
          <Button variant="secondary" onClick={() => setCallOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void bridgeCall()}>
            Confirm
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={confirmSave}
        toggle={() => !saveBusy && setConfirmSave(false)}
        centered
        className="ds-dialog"
      >
        <h2 className="ds-dialog__title">Confirm save</h2>
        <div className="ds-dialog__body">Update this lead?</div>
        <div className="ds-dialog__actions">
          <Button variant="secondary" disabled={saveBusy} onClick={() => setConfirmSave(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={saveBusy} onClick={() => void runSave()}>
            Save
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={historyOpen}
        toggle={() => setHistoryOpen(false)}
        size="lg"
        centered
        className="ds-dialog"
      >
        <h2 className="ds-dialog__title">Lead history</h2>
        {historyLoading ? (
          <Loader label="Loading history…" />
        ) : historyRows.length === 0 ? (
          <EmptyState
            title="No audit entries yet."
            description="Changes to this lead's statuses and comments will show up here."
          />
        ) : (
          <div className="demo-ops-scroll">
            <Table caption="Lead history">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Old</th>
                  <th>New</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.map((r, i) => (
                  <tr key={`${r.field}-${i}`}>
                    <td>{r.field}</td>
                    <td>{r.old_value}</td>
                    <td>{r.new_value}</td>
                    <td>
                      {r.createdOn
                        ? moment(r.createdOn).isValid()
                          ? moment(r.createdOn).format("DD-MMM-YYYY HH:mm:ss")
                          : r.createdOn
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <div className="ds-dialog__actions">
          <Button variant="secondary" onClick={() => setHistoryOpen(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </Page>
  );
};

export default DetailViewOps;
